import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Subject, Topic, Course, Module } from '../types/microsoftGraph';
import { api, API_ENDPOINTS } from '../config/api';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const SubjectsAndTopics: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [folderSetupLoading, setFolderSetupLoading] = useState<number | null>(null);

  // Dialog states
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);

  // Selected items
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

  // Filter states
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState<number | null>(null);
  const [selectedCourseFilter, setSelectedCourseFilter] = useState<number | null>(null);
  const [selectedModuleFilter, setSelectedModuleFilter] = useState<number | null>(null);

  // Form states
  const [subjectForm, setSubjectForm] = useState({ Subject_name: '', Subject_code: '', SubjectID: 0 });
  const [courseForm, setCourseForm] = useState({ Course_name: '', Course_code: '', SubjectID: 0 });
  const [moduleForm, setModuleForm] = useState({ Module_name: '', Module_code: '', CourseID: 0 });
  const [topicForm, setTopicForm] = useState({ Topic_name: '', Topic_code: '', ModuleID: 0 });

  // Use app auth to obtain token and build request headers
  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subjectsData, coursesData] = await Promise.all([
        api.get(API_ENDPOINTS.subjects.list),
        api.get(API_ENDPOINTS.courses.list),
      ]);
      
      console.log('Fetched Subjects:', subjectsData);
      console.log('Fetched Courses:', coursesData);
      
      setSubjects(subjectsData || []);
      setCourses(coursesData || []);
      
      setError(null);
    } catch (err) {
      console.error('[SubjectsAndTopics] fetchData ERROR:', err);
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Subject handlers
  const handleAddSubject = () => {
    setSelectedSubject(null);
    setSubjectForm({ Subject_name: '', Subject_code: '', SubjectID: 0 });
    setSubjectDialogOpen(true);
  };

  const handleEditSubject = (subject: Subject) => {
    setSelectedSubject(subject);
    setSubjectForm({ Subject_name: subject.Subject_name, Subject_code: subject.Subject_code, SubjectID: subject.SubjectID });
    setSubjectDialogOpen(true);
  };

  const handleSaveSubject = async () => {
    if (!subjectForm.Subject_name || !subjectForm.Subject_code) {
      setError('Please fill all required fields');
      return;
    }

    try {
      const endpoint = selectedSubject
        ? API_ENDPOINTS.subjects.update(selectedSubject.SubjectID)
        : API_ENDPOINTS.subjects.create;

      await (selectedSubject
        ? api.put(endpoint, subjectForm)
        : api.post(endpoint, subjectForm)
      );

      setSuccess(selectedSubject ? 'Subject updated successfully' : 'Subject created successfully');
      setSubjectDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save subject');
      console.error(err);
    }
  };

  const handleDeleteSubject = async (id: number) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await api.delete(API_ENDPOINTS.subjects.delete(id));
      setSuccess('Subject deleted successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete subject');
      console.error(err);
    }
  };

  // Course handlers
  const handleAddCourse = () => {
    setSelectedCourse(null);
    setCourseForm({ Course_name: '', Course_code: '', SubjectID: selectedSubjectFilter || 0 });
    setCourseDialogOpen(true);
  };

  const handleEditCourse = (course: Course) => {
    setSelectedCourse(course);
    setCourseForm({ Course_name: course.Course_name, Course_code: course.Course_code, SubjectID: course.SubjectID });
    setCourseDialogOpen(true);
  };

  const handleSaveCourse = async () => {
    if (!courseForm.Course_name || !courseForm.Course_code || !courseForm.SubjectID) {
      setError('Please fill all required fields');
      return;
    }

    try {
      const endpoint = selectedCourse
        ? API_ENDPOINTS.courses.update(selectedCourse.CourseID)
        : API_ENDPOINTS.courses.create;

      await (selectedCourse
        ? api.put(endpoint, courseForm)
        : api.post(endpoint, courseForm)
      );

      setSuccess(selectedCourse ? 'Course updated successfully' : 'Course created successfully');
      setCourseDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save course');
      console.error(err);
    }
  };

  const handleDeleteCourse = async (id: number) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await api.delete(API_ENDPOINTS.courses.delete(id));
      setSuccess('Course deleted successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete course');
      console.error(err);
    }
  };

  // Module handlers
  const handleAddModule = () => {
    setSelectedModule(null);
    setModuleForm({ Module_name: '', Module_code: '', CourseID: selectedCourseFilter || 0 });
    setModuleDialogOpen(true);
  };

  const handleEditModule = (module: Module) => {
    setSelectedModule(module);
    setModuleForm({ Module_name: module.Module_name, Module_code: module.Module_code, CourseID: module.CourseID });
    setModuleDialogOpen(true);
  };

  const handleSaveModule = async () => {
    if (!moduleForm.Module_name || !moduleForm.Module_code || !moduleForm.CourseID) {
      setError('Please fill all required fields');
      return;
    }

    try {
      const endpoint = selectedModule
        ? `${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/modules/${selectedModule.ModuleID}`
        : `${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/modules`;

      await (selectedModule
        ? api.put(endpoint, moduleForm)
        : api.post(endpoint, moduleForm)
      );

      setSuccess(selectedModule ? 'Module updated successfully' : 'Module created successfully');
      setModuleDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save module');
      console.error(err);
    }
  };

  const handleDeleteModule = async (id: number) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await api.delete(`${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/modules/${id}`);
      setSuccess('Module deleted successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete module');
      console.error(err);
    }
  };

  // Topic handlers
  const handleAddTopic = () => {
    setSelectedTopic(null);
    setTopicForm({ Topic_name: '', Topic_code: '', ModuleID: selectedModuleFilter || 0 });
    setTopicDialogOpen(true);
  };

  const handleEditTopic = (topic: Topic) => {
    setSelectedTopic(topic);
    setTopicForm({
      Topic_name: topic.Topic_name,
      Topic_code: topic.Topic_code,
      ModuleID: topic.ModuleID,
    });
    setTopicDialogOpen(true);
  };

  const handleSaveTopic = async () => {
    if (!topicForm.Topic_name || !topicForm.Topic_code || topicForm.ModuleID === 0) {
      setError('Please fill all required fields');
      return;
    }

    try {
      const endpoint = selectedTopic
        ? `${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/topics/${selectedTopic.TopicID}`
        : `${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/topics`;

      await (selectedTopic
        ? api.put(endpoint, topicForm)
        : api.post(endpoint, topicForm)
      );

      setSuccess(selectedTopic ? 'Topic updated successfully' : 'Topic created successfully');
      setTopicDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save topic');
      console.error(err);
    }
  };

  const handleDeleteTopic = async (id: number) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await api.delete(`${API_ENDPOINTS.courses.list.replace('/coursesALL', '')}/topics/${id}`);
      setSuccess('Topic deleted successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete topic');
      console.error(err);
    }
  };

  const handleSetupFolder = async (subject: Subject) => {
    if (!window.confirm(`This will create a folder structure for "${subject.Subject_name}" in SharePoint. Continue?`)) {
      return;
    }

    setFolderSetupLoading(subject.SubjectID);
    try {
      const resData = await api.post(
        `${API_ENDPOINTS.subjects.get(subject.SubjectID)}`.replace('/subject/', '/subjects/setup-folder/')
      );

      setSuccess(resData.message || 'Folder structure created successfully!');
      fetchData();
    } catch (err) {
      setError((err as Error).message);
      console.error(err);
    } finally {
      setFolderSetupLoading(null);
    }
  };

  // Helper functions
  const filteredCourses = selectedSubjectFilter 
    ? courses.filter(c => c.SubjectID === selectedSubjectFilter)
    : [];

  const filteredModules = selectedCourseFilter
    ? modules.filter(m => m.CourseID === selectedCourseFilter)
    : [];

  const filteredTopics = selectedModuleFilter
    ? topics.filter(t => t.ModuleID === selectedModuleFilter)
    : [];

  if (loading) return <CircularProgress />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Top header with navigation action */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                                        <ArrowBackIcon />
                                    </IconButton>
        <Typography variant="h4">Subjects & Topics</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => navigate('/assign-skill')}
          disabled={!token}
        >
          Assign Skills
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Subjects Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Subjects"
          action={
            <Button startIcon={<AddIcon />} onClick={handleAddSubject} variant="contained">
              Add Subject
            </Button>
          }
        />
        <CardContent>
          {subjects.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center', color: '#999' }}>
              No subjects yet. Click "Add Subject" to create one.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableBody>
                  {subjects.map((subject) => (
                    <TableRow 
                      key={`subject-${subject.SubjectID}`}
                      hover
                      sx={{ cursor: 'pointer', backgroundColor: selectedSubjectFilter === subject.SubjectID ? '#e3f2fd' : 'inherit' }}
                      onClick={() => {
                        setSelectedSubjectFilter(selectedSubjectFilter === subject.SubjectID ? null : subject.SubjectID);
                        setSelectedCourseFilter(null);
                        setSelectedModuleFilter(null);
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {subject.Subject_name || '(No name)'}
                      </TableCell>
                      <TableCell>{subject.Subject_code}</TableCell>
                      <TableCell align="right" sx={{ width: '180px' }}>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetupFolder(subject);
                          }}
                          disabled={folderSetupLoading === subject.SubjectID}
                          title="Create folder structure in SharePoint"
                        >
                          {folderSetupLoading === subject.SubjectID ? (
                            <CircularProgress size={24} />
                          ) : (
                            <FolderIcon />
                          )}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSubject(subject);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSubject(subject.SubjectID);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Courses Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={selectedSubjectFilter ? `Courses (${subjects.find(s => s.SubjectID === selectedSubjectFilter)?.Subject_name})` : "Courses - Click a subject to filter"}
          action={
            <Button 
              startIcon={<AddIcon />} 
              onClick={handleAddCourse} 
              variant="contained"
              disabled={!selectedSubjectFilter}
            >
              Add Course
            </Button>
          }
        />
        <CardContent>
          {!selectedSubjectFilter ? (
            <Typography sx={{ p: 2, textAlign: 'center', color: '#999' }}>
              Click on a subject above to view its courses
            </Typography>
          ) : filteredCourses.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center', color: '#999' }}>
              No courses in this subject
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {filteredCourses.map((course) => (
                    <TableRow 
                      key={`course-${course.CourseID}`}
                      hover
                      sx={{ cursor: 'pointer', backgroundColor: selectedCourseFilter === course.CourseID ? '#f3e5f5' : 'inherit' }}
                      onClick={() => {
                        setSelectedCourseFilter(selectedCourseFilter === course.CourseID ? null : course.CourseID);
                        setSelectedModuleFilter(null);
                      }}
                    >
                      <TableCell sx={{ pl: 4 }}>
                        {course.Course_name || '(No name)'}
                      </TableCell>
                      <TableCell>{course.Course_code}</TableCell>
                      <TableCell align="right" sx={{ width: '120px' }}>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCourse(course);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCourse(course.CourseID);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Subject Dialog */}
      <Dialog open={subjectDialogOpen} onClose={() => setSubjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Subject Name"
              value={subjectForm.Subject_name}
              onChange={(e) => setSubjectForm({ ...subjectForm, Subject_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Subject Code"
              value={subjectForm.Subject_code}
              onChange={(e) => setSubjectForm({ ...subjectForm, Subject_code: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSubject} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Dialog */}
      <Dialog open={courseDialogOpen} onClose={() => setCourseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCourse ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Course Name"
              value={courseForm.Course_name}
              onChange={e => setCourseForm({ ...courseForm, Course_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Course Code"
              value={courseForm.Course_code}
              onChange={e => setCourseForm({ ...courseForm, Course_code: e.target.value })}
              fullWidth
            />
            {selectedCourse && (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                Subject: <strong>{subjects.find(s => s.SubjectID === courseForm.SubjectID)?.Subject_name}</strong>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourseDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCourse} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default SubjectsAndTopics;